export interface JWTHeader {
  alg: string;
  typ: string;
  kid?: string;
}

export interface JWTPayload {
  sub?: string;
  oid?: string;
  email?: string;
  name?: string;
  tid?: string;
  iat?: number;
  exp?: number;
  aud?: string;
  iss?: string;
  scp?: string;
  roles?: string[];
}

export interface DecodedJWT {
  header: JWTHeader;
  payload: JWTPayload;
  signature: string;
}

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  roles?: string[];
  scopes?: string[];
}

export class JWTDecoder {
  /**
   * Properly decode base64 with UTF-8 support
   */
  private static base64DecodeUTF8(str: string): string {
    try {
      // First decode base64 to bytes
      const binaryString = atob(str);
      // Then convert bytes to UTF-8 string
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch {
      // Fallback to regular atob if UTF-8 decoding fails
      return atob(str);
    }
  }

  static decode(token: string): DecodedJWT | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const [headerB64, payloadB64, signature] = parts;
      
      const header = JSON.parse(this.base64DecodeUTF8(headerB64)) as JWTHeader;
      const payload = JSON.parse(this.base64DecodeUTF8(payloadB64)) as JWTPayload;

      return {
        header,
        payload,
        signature
      };
    } catch {
      return null;
    }
  }

  static extractUserInfo(token: string): UserInfo | null {
    const decoded = this.decode(token);
    if (!decoded) {
      return null;
    }

    const { payload } = decoded;
    
    // Ensure required fields are present
    if (!payload.oid && !payload.sub) {
      return null;
    }

    return {
      id: payload.oid || payload.sub || '',
      email: payload.email || '',
      name: payload.name || '',
      tenantId: payload.tid || '',
      roles: payload.roles || [],
      scopes: payload.scp?.split(' ') || []
    };
  }

  static isExpired(token: string): boolean {
    const decoded = this.decode(token);
    if (!decoded?.payload.exp) {
      return true;
    }

    return Date.now() >= decoded.payload.exp * 1000;
  }

  static getExpiryDate(token: string): Date | null {
    const decoded = this.decode(token);
    if (!decoded?.payload.exp) {
      return null;
    }

    return new Date(decoded.payload.exp * 1000);
  }
}